import { cn } from "@/lib/utils";

interface ListingTypeOption {
  id: string;
  label: string;
  value: string;
  count?: number;
}

interface ListingTypeSectionProps {
  title: string;
  options: ListingTypeOption[];
  selectedValue: string | null;
  onSelect: (value: string | null) => void;
  variant?: 'sell' | 'buy';
  assetType?: 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services';
  onLabelClick?: () => void;
  isLabelActive?: boolean;
}

export const ListingTypeSection = ({
  title,
  options,
  selectedValue,
  onSelect,
  variant = 'sell',
  assetType,
  onLabelClick,
  isLabelActive
}: ListingTypeSectionProps) => {
  // Determine display title based on asset type and variant
  let displayTitle = title;
  if (assetType === 'services') {
    displayTitle = variant === 'sell' ? 'Providers' : 'Seekers';
  } else if (assetType === 'jobs') {
    displayTitle = variant === 'sell' ? 'Companies' : 'Professionals';
  }

  const isSellVariant = variant === 'sell';
  
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-3">
      {/* Section label with accent bar */}
      <div className="flex items-center gap-2 sm:flex-shrink-0">
        <div className={cn(
          "w-1 h-5 rounded-full transition-all duration-300",
          isSellVariant 
            ? "bg-gradient-to-b from-emerald-400 to-emerald-600" 
            : "bg-gradient-to-b from-amber-400 to-amber-600"
        )} />
        <button
          onClick={onLabelClick}
          className={cn(
            "text-[11px] font-bold uppercase tracking-widest transition-all cursor-pointer whitespace-nowrap",
            "hover:opacity-80 active:scale-95",
            isSellVariant 
              ? "text-emerald-600 dark:text-emerald-400" 
              : "text-amber-600 dark:text-amber-400",
            isLabelActive && cn(
              "px-2 py-0.5 rounded-full",
              isSellVariant
                ? "bg-emerald-100 dark:bg-emerald-900/40"
                : "bg-amber-100 dark:bg-amber-900/40"
            )
          )}
        >
          {displayTitle}
        </button>
      </div>
      
      {/* Filter pills with enhanced styling */}
      <div className="flex gap-2 flex-wrap">
        {options.map((option) => {
          const isSelected = selectedValue === option.value;
          return (
            <button
              key={option.id}
              onClick={() => onSelect(isSelected ? null : option.value)}
              className={cn(
                "px-3.5 py-2 rounded-full text-xs font-semibold",
                "transition-all duration-200 ease-out",
                "border-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                isSelected ? cn(
                  isSellVariant 
                    ? "bg-gradient-to-r from-emerald-500 to-emerald-600 text-white border-emerald-400 shadow-lg shadow-emerald-500/25"
                    : "bg-gradient-to-r from-amber-500 to-amber-600 text-white border-amber-400 shadow-lg shadow-amber-500/25",
                  "scale-105"
                ) : cn(
                  isSellVariant
                    ? "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800 hover:border-emerald-400 hover:shadow-md"
                    : "bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800 hover:border-amber-400 hover:shadow-md"
                )
              )}
            >
              {option.label}
              {option.count !== undefined && option.count > 0 && (
                <span className={cn(
                  "ml-1.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold",
                  isSelected 
                    ? "bg-white/25" 
                    : isSellVariant 
                      ? "bg-emerald-200 dark:bg-emerald-800" 
                      : "bg-amber-200 dark:bg-amber-800"
                )}>
                  {option.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
